import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import isNumber from 'lodash/isNumber';
import sortBy from 'lodash/sortBy';
import maxBy from 'lodash/maxBy';
import capitalize from 'lodash/capitalize';
import has from 'lodash/has';
import SelectionModal from '../SelectionModal';
// icons
import MfeDashFav2Icon from 'zignal-landscape/lib/Icon/MfeDashFav2Icon';
import MfeNavUserIcon from 'zignal-landscape/lib/Icon/MfeNavUserIcon';
import MfeDashSharedByOthersIcon from 'zignal-landscape/lib/Icon/MfeDashSharedByOthersIcon';
import MfeDashMySharedDashIcon from 'zignal-landscape/lib/Icon/MfeDashMySharedDashIcon';
import MfeDashAllIcon from 'zignal-landscape/lib/Icon/MfeDashAllIcon';

export const getFilters = (modalType) => [
  {label: 'Workspace favorites', icon: MfeDashFav2Icon },
  {label: 'Shared by others', icon: MfeDashSharedByOthersIcon },
  {label: `My ${modalType}`, icon: MfeNavUserIcon },
  {label: `My shared ${modalType}`, icon: MfeDashMySharedDashIcon },
  {label: 'All', icon: MfeDashAllIcon },
];

async function saveFavorite(model, campaignFavoriteSortOrder, refreshCollectionFn) {
  try {
    const id = model.get('id');
    const res = await fetch(`/api/widget-annotations/dashboards/favorite/${id}`, {
      method: 'POST',
      body: JSON.stringify({ campaignFavoriteSortOrder }),
    });
    const json = await res.json();
    // could be an error, so check that the field exists in the response.
    // if an error, for now do nothing. it's an edge case that we probably don't need to worry about.
    if (has(json, 'campaignFavoriteSortOrder')) {
      // setting or removing campaign favorite will always make dashboard public
      model.set({ campaignFavoriteSortOrder: json.campaignFavoriteSortOrder, public: true });
      refreshCollectionFn();
    }
  } catch (e) {
    return;
  }
}

export const getFilterData = (model, modalType) => {
  const { id: userId } = Account.get('user') || {};
  const { user, public: shared, campaignFavoriteSortOrder } = model;
  const isOwner = userId && userId === user;
  const isFavorited = isNumber(campaignFavoriteSortOrder);
  let icon = MfeNavUserIcon;
  const filterTypes = [];
  if (shared) {
    icon = isOwner ? MfeDashMySharedDashIcon : MfeDashSharedByOthersIcon;
    filterTypes.push(isOwner ? `My shared ${modalType}` : 'Shared by others');
    if (isFavorited) {
      filterTypes.push('Workspace favorites');
      icon = MfeDashFav2Icon;
    }
  } else {
    filterTypes.push(`My ${modalType}`);
  }
  return { filterTypes, icon, isFavorited, isOwner };
};

const DEFAULT_PARAMS = [];

const WidgetAnnotationModal = ({
  params = DEFAULT_PARAMS,
  onClose = noop,
  navigateBackBone = noop,
  newDashboardOrReport = noop,
  modelType = '',
  baseUrlLabel = ''
}) => {
  const baseUrl = `/${modelType}/`;
  const modalType = `${modelType}s`;
  const title = capitalize(modalType);

  const [refreshCollection, setRefreshCollection] = useState(false);
  const [isTakingAction, setIsTakingAction] = useState(false);
  const isCampaignAdmin = Account.get('user').hasMinimumRole('ROLE_CAMPAIGN_ADMIN');
  const collection = Account.get(modalType);
  const models = collection ? collection.toJSON() : [];
  const isCurrentView = params[0] === modelType;
  const widgetAnnotationId = isCurrentView ? params[1] : null;
  // I am assigning the models an increasing sort order, but at this time, the order is being ignored.
  // in the future, we may allow users to change the order of favorites. for not, it only matters if
  // campaignFavoriteSortOrder is a number or not.
  const campaignFavorites = models.filter((model) => isNumber(model.campaignFavoriteSortOrder));
  let highestFavoriteSortOrder = (maxBy(campaignFavorites, 'campaignFavoriteSortOrder') || {}).campaignFavoriteSortOrder;
  highestFavoriteSortOrder = isNumber(highestFavoriteSortOrder) ? highestFavoriteSortOrder : -1;

  const onFavoriteItem = useCallback((e, {id, model: { campaignFavoriteSortOrder }}) => {
    if (isTakingAction) {
      return;
    }
    setIsTakingAction(true);
    const isRemoving = isNumber(campaignFavoriteSortOrder);
    // leaving this here for now if we want to limit the number of favorites
    // if (!isRemoving && campaignFavorites.length > 10) {
    //   return alert('You can only have 10 campaign favorites. Please remove one before adding another.');
    // }
    const message = isRemoving
      ? `Are you sure you want to remove this ${modelType} from the Workspace Favorites? This will affect all users of this workspace.`
      : `Are you sure you want to add this ${modelType} to the Workspace Favorites? This will affect all users of this workspace.'`;
    if (confirm(message)) {
      const model = collection.find((model) => model.get('id') === id);
      const updatedCampaignFavoriteSortOrder = isRemoving ? null : highestFavoriteSortOrder + 1;
      const callback = () => {
        setRefreshCollection(!refreshCollection);
        setIsTakingAction(false);
      };
      saveFavorite(model, updatedCampaignFavoriteSortOrder, callback);
    } else {
      setIsTakingAction(false);
    }
  }, [collection, isTakingAction, setRefreshCollection, setIsTakingAction, highestFavoriteSortOrder, campaignFavorites]);

  const onDeleteItem = useCallback((e, {id, active}) => {
    if (isTakingAction) {
      return;
    }
    setIsTakingAction(true);
    if (confirm(`Are you sure you want to delete this ${modelType}?`)) {
      const model = collection.find((model) => model.get('id') === id);
      model.destroy({
        wait: true,
        success: () => {
          setRefreshCollection(!refreshCollection);
          setIsTakingAction(false);
          if (active) {
            navigateBackBone(e, baseUrl);
          }
        },
        failure: () => {
          setRefreshCollection(!refreshCollection);
          setIsTakingAction(false);
        }
      });
    } else {
      setIsTakingAction(false);
    }
  }, [collection, navigateBackBone, isTakingAction, setRefreshCollection, setIsTakingAction]);

  const defaultDashboard = {
    id: 'default', label: baseUrlLabel, active: isCurrentView && !widgetAnnotationId,
    href: baseUrl, filterTypes: ['Workspace favorites'], icon: MfeDashFav2Icon
  };

  const items = sortBy(models.map((model) => {
    const { id, data={} } = model;
    const { title: label } = data;
    const active = widgetAnnotationId && widgetAnnotationId === id;
    const filterData = getFilterData(model, modalType);
    const href = `${baseUrl}${id}/`;
    const sortLabel = label.toLowerCase();
    return { id, active, label, sortLabel, href, model, ...filterData };
  }), 'sortLabel');

  const filters = getFilters(modalType);
  const onClickItem = useCallback((e) => {
    onClose();
    navigateBackBone(e);
  });
  const onAddItem = useCallback((e) => {
    onClose();
    newDashboardOrReport(e, modelType);
  });

  return (<SelectionModal
    onClose={onClose}
    title={title}
    items={items}
    filters={filters}
    onClickItem={onClickItem}
    onAddItem={onAddItem}
    defaultView={defaultDashboard}
    onDeleteItem={onDeleteItem}
    onFavoriteItem={onFavoriteItem}
    isCampaignAdmin={isCampaignAdmin}
  />);
};

WidgetAnnotationModal.propTypes = {
  params: PropTypes.array,
  modelType: PropTypes.string,
  baseUrlLabel: PropTypes.string,
  onClose: PropTypes.func,
  navigateBackBone: PropTypes.func,
  newDashboardOrReport: PropTypes.func
};

export default WidgetAnnotationModal;
